import {
  Simulation,
  Evaluator,
  type PromptImproverSuggestion,
} from '../../lib/types';
import { formatEvaluationScore, isNotApplicableScore } from '../../lib/utils';

const buildCsvHeader = (
  includeSuggestions: boolean,
  includePersonaTag: boolean,
): string => {
  const columns = [
    'Agent Name',
    'Agent Prompt',
    'Persona Name',
    ...(includePersonaTag ? ['Persona Tag'] : []),
    'Agent Version',
    'Status',
    'Failure Reason',
    'Evaluation Type',
    'Statement',
    'Score',
    'Success',
    'Failure',
    'Not Applicable',
    ...(includeSuggestions ? ['Improvement Suggestions'] : []),
    'Number of Turns',
    'Conversation',
    'Evaluated At',
  ];
  return columns.join(',');
};

const hasAnyImprovementSuggestions = (
  simulations: Simulation[],
  visibleEvaluators: Evaluator[],
): boolean => {
  const evaluatorIds = new Set(visibleEvaluators.map((e) => e.id));
  for (const simulation of simulations) {
    const evaluations = simulation.evaluations;
    if (!evaluations) continue;
    for (const [evaluatorId, evaluation] of Object.entries(evaluations)) {
      if (evaluatorIds.has(evaluatorId) && evaluation.suggestions?.length)
        return true;
    }
  }
  return false;
};

const formatSuggestionsForCsv = (
  suggestions: PromptImproverSuggestion[] | undefined,
): string => {
  if (!suggestions?.length) return '';
  return suggestions
    .map((s, index) => {
      const consider = s.consider.join('; ');
      return [
        `Suggestion ${index + 1}: ${s.title}`,
        `Issue: ${s.issue}`,
        `Potential impact: ${s.potential_impact}`,
        `Consider: ${consider}`,
      ].join('\n');
    })
    .join('\n\n');
};

const escapeCsvField = (value: string): string =>
  `"${(value || '').replace(/"/g, '""')}"`;

const findEvaluatorById = (
  evaluators: Evaluator[],
  evaluatorId: string,
): Evaluator | undefined =>
  evaluators.find((evaluator) => evaluator.id === evaluatorId);

const buildSimulationsCsvInternal = (
  simulations: Simulation[],
  visibleEvaluators: Evaluator[],
  getAgentName: (simulation: Simulation) => string | undefined,
): string => {
  const includeSuggestionsColumn = hasAnyImprovementSuggestions(
    simulations,
    visibleEvaluators,
  );
  const includePersonaTag = simulations.some((s) => !!s.persona?.short_tag);
  const header = buildCsvHeader(includeSuggestionsColumn, includePersonaTag);

  let csvContent = `${header}\n`;

  simulations.forEach((simulation) => {
    const evaluations = simulation.evaluations || {};
    const runFields = [
      simulation.agent_version ? `v${simulation.agent_version}` : '',
      escapeCsvField(simulation.status ?? 'complete'),
      escapeCsvField(simulation.failure_reason ?? ''),
    ];

    const conversationText = simulation.messages
      .map(
        (message) => `${message.role.toUpperCase()}: ${message.content || ''}`,
      )
      .join('\n\n')
      .replace(/"/g, '""');

    Object.entries(evaluations).forEach(([evaluatorId, evaluation]) => {
      const evaluator = findEvaluatorById(visibleEvaluators, evaluatorId);
      if (!evaluator) return;

      const baseFields = [
        escapeCsvField(getAgentName(simulation) || 'Unknown Agent'),
        escapeCsvField(simulation.agent?.prompt || ''),
        escapeCsvField(simulation.persona?.name || 'Unknown Persona'),
        ...(includePersonaTag
          ? [escapeCsvField(simulation.persona?.short_tag || '')]
          : []),
        ...runFields,
        escapeCsvField(evaluator.name),
        escapeCsvField(evaluator.statement),
        formatEvaluationScore(evaluation.score),
        escapeCsvField(
          isNotApplicableScore(evaluation.score)
            ? ''
            : evaluation.success || '',
        ),
        escapeCsvField(
          isNotApplicableScore(evaluation.score)
            ? ''
            : evaluation.failure || '',
        ),
        escapeCsvField(
          isNotApplicableScore(evaluation.score)
            ? evaluation.not_tested || ''
            : '',
        ),
      ];

      const numberOfTurns = String(Math.floor(simulation.messages.length / 2));
      const evaluatedAt = evaluation.evaluated_at
        ? new Date(evaluation.evaluated_at).toLocaleString()
        : '';

      const rowFields = includeSuggestionsColumn
        ? [
            ...baseFields,
            escapeCsvField(formatSuggestionsForCsv(evaluation.suggestions)),
            numberOfTurns,
            escapeCsvField(conversationText),
            escapeCsvField(evaluatedAt),
          ]
        : [
            ...baseFields,
            numberOfTurns,
            escapeCsvField(conversationText),
            escapeCsvField(evaluatedAt),
          ];

      csvContent += rowFields.join(',') + '\n';
    });

    // A failed run can't be scored, but it still belongs in the record: one
    // row with its status and reason, and no evaluation.
    if (simulation.status === 'failed' && !Object.keys(evaluations).length) {
      const blankEvaluation = ['', '', '', '', '', ''];
      csvContent +=
        [
          escapeCsvField(getAgentName(simulation) || 'Unknown Agent'),
          escapeCsvField(simulation.agent?.prompt || ''),
          escapeCsvField(simulation.persona?.name || 'Unknown Persona'),
          ...(includePersonaTag
            ? [escapeCsvField(simulation.persona?.short_tag || '')]
            : []),
          ...runFields,
          ...blankEvaluation,
          ...(includeSuggestionsColumn ? [''] : []),
          String(Math.floor(simulation.messages.length / 2)),
          escapeCsvField(conversationText),
          '',
        ].join(',') + '\n';
    }
  });

  return csvContent;
};

export const buildSimulationCsv = (
  simulation: Simulation,
  visibleEvaluators: Evaluator[],
): string =>
  buildSimulationsCsvInternal(
    [simulation],
    visibleEvaluators,
    (sim) => sim.agent?.name,
  );

export const buildAgentSimulationsCsv = (
  agentName: string | undefined,
  simulations: Simulation[],
  visibleEvaluators: Evaluator[],
): string =>
  buildSimulationsCsvInternal(simulations, visibleEvaluators, () => agentName);

export const downloadCsv = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
